import { isValidElement, useMemo, type ReactNode } from 'react';
import { aiAssistantRoutes } from '@/routes/siteMaps';
import { useListMcpThreadsQuery } from '../api/mcpApi';
import { getThreadTitlePresentation } from '../utils/threadHistoryDisplay';

/** Max recent threads under «سابقه چت» (nav and chat sidebar). */
export const AI_NAV_HISTORY_LIMIT = 8 as const;

/** Max characters for thread titles in the main sidebar. */
const AI_NAV_TITLE_MAX_CHARS = 18;

type RouteNode = {
  name: string;
  to?: string;
  exact?: boolean;
  active?: boolean;
  icon?: string | ReactNode;
  disabled?: boolean;
  defaultOpen?: boolean;
  children?: RouteNode[];
};

/** structuredClone rejects React elements (icon components). Keep those by reference. */
function cloneNavSection<T>(value: T): T {
  if (isValidElement(value) || value == null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(item => cloneNavSection(item)) as T;
  }
  const copy: Record<string, unknown> = {};
  for (const key of Object.keys(value as object)) {
    const child = (value as Record<string, unknown>)[key];
    copy[key] =
      key === 'icon' || isValidElement(child) ? child : cloneNavSection(child);
  }
  return copy as T;
}

function displayTitle(title: string | null): string {
  const pres = getThreadTitlePresentation(title);
  const text =
    pres.kind === 'chat'
      ? pres.label
      : pres.context
        ? `${pres.label} · ${pres.context}`
        : pres.label;
  return text.length > AI_NAV_TITLE_MAX_CHARS
    ? `${text.slice(0, AI_NAV_TITLE_MAX_CHARS)}…`
    : text;
}

/** Loads chat history only while the AI sidebar is actually visible. */
export function useAiAssistantNavRoutes(enabled = true) {
  const { data } = useListMcpThreadsQuery({
    limit: AI_NAV_HISTORY_LIMIT,
    offset: 0
  }, {
    skip: !enabled
  });
  const threads = (data?.items ?? []).slice(0, AI_NAV_HISTORY_LIMIT);

  return useMemo(() => {
    const section = cloneNavSection(aiAssistantRoutes) as {
      label: string;
      labelDisable?: boolean;
      children: RouteNode[];
    };

    const historyNode = section.children?.find(
      child => child.name === 'سابقه چت'
    );

    if (historyNode) {
      historyNode.children =
        threads.length === 0
          ? [
              {
                name: 'سابقه ای یافت نشد',
                to: '#!',
                exact: true,
                active: false,
                disabled: true
              }
            ]
          : [
              ...threads.map(thread => ({
                name: displayTitle(thread.title),
                to: `/chat/c/${thread.id}`,
                exact: true,
                active: true
              })),
              {
                name: 'بیشتر',
                to: '/chat/history',
                exact: true,
                active: true
              }
            ];
    }

    return section;
  }, [threads]);
}
